import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, BookOpen, MonitorPlay } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { useLang } from '@/contexts/LangContext';
import { buildImageUrl, normalizeLocalizedData, repairArabicMojibake } from '@/lib/utils';

function pickText(lang, arText, enText) {
  return lang === 'ar' ? repairArabicMojibake(arText) : enText;
}

export default function CoursesPage() {
  const { t, lang } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [languages, setLanguages] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState(searchParams.get('language_id') || 'all');

  useEffect(() => {
    api.get('/languages')
      .then((res) => setLanguages(normalizeLocalizedData(Array.isArray(res.data) ? res.data : [])))
      .catch(() => setLanguages([]));
  }, [lang]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (languageFilter && languageFilter !== 'all') {
      nextParams.set('language_id', languageFilter);
    } else {
      nextParams.delete('language_id');
    }
    setSearchParams(nextParams, { replace: true });
  }, [languageFilter, searchParams, setSearchParams]);

  useEffect(() => {
    setLoading(true);
    api.get('/courses', {
      params: {
        audience: 'individual',
        language_id: languageFilter !== 'all' ? languageFilter : undefined,
      },
    })
      .then((res) => setCourses(normalizeLocalizedData(Array.isArray(res.data) ? res.data : [])))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, [lang, languageFilter]);

  const courseCards = useMemo(() => courses.map((course) => ({
    ...course,
    href: `/courses/${course.slug}`,
  })), [courses]);

  return (
    <div data-testid="courses-page" className="bf-page-canvas bf-courses-page bg-[#f5f6f8] text-[#231f20] dark:bg-[#231f20] dark:text-[#e6e6e6]">
      <section className="relative overflow-hidden px-4 pb-14 pt-24 sm:px-6 lg:px-8 lg:pb-20 lg:pt-28">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="bf-brand-kicker text-[#6c8bc6]">
              {pickText(lang, 'الدورات', 'Courses')}
            </p>
            <h1 className="bf-page-display mt-3">
              {pickText(lang, 'استكشف برامجنا اللغوية', 'Explore Our Language Programs')}
            </h1>
            <p className="bf-page-copy mt-4 max-w-2xl text-[#231f20]/70 dark:text-[#e6e6e6]/72">
              {pickText(
                lang,
                'تصفح الدورات المتاحة واختر البرنامج الأنسب لأهدافك ومستواك.',
                'Browse the available courses and choose the program that fits your goals and level.'
              )}
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 max-w-sm">
            <p className="bf-ui-label mb-2 text-[#6c8bc6]">
              {pickText(lang, 'اللغة', 'Language')}
            </p>
            <Select value={languageFilter} onValueChange={setLanguageFilter}>
              <SelectTrigger className="bf-filter-control h-12 rounded-2xl bg-white dark:bg-white/5">
                <SelectValue placeholder={pickText(lang, 'اختر اللغة', 'Choose language')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{pickText(lang, 'كل اللغات', 'All languages')}</SelectItem>
                {languages.map((language) => (
                  <SelectItem key={language.id} value={String(language.id)}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <p className="bf-ui-label text-[#6c8bc6]">
                {pickText(lang, 'النتائج', 'Results')}
              </p>
              <h2 className="bf-subsection-display mt-2">
                {pickText(lang, 'كل الدورات المتاحة', 'All Available Courses')}
              </h2>
            </div>
            <span className="bf-meta-copy text-[#231f20]/60 dark:text-[#e6e6e6]/62">
              {courseCards.length} {t('courses.found')}
            </span>
          </div>

          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <Card key={item} className="animate-pulse overflow-hidden rounded-[2rem]">
                  <div className="h-56 bg-muted" />
                  <CardContent className="space-y-3 p-6">
                    <div className="h-4 w-1/2 rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : courseCards.length === 0 ? (
            <div className="bf-empty-state rounded-[2rem] border border-dashed border-[#cdd4de] bg-white/60 px-6 py-16 text-center dark:border-white/10 dark:bg-white/5">
              <BookOpen className="mx-auto h-12 w-12 text-[#6c8bc6]" />
              <h3 className="bf-card-display mt-4 text-xl">
                {pickText(lang, 'لا توجد دورات متاحة حالياً', 'No courses available right now')}
              </h3>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {courseCards.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={course.href} className="block h-full">
                    <Card className="bf-course-card group flex h-full flex-col overflow-hidden rounded-[2rem] border-0 bg-white dark:bg-white/5 dark:text-white">
                      <div className="bf-course-card-media relative h-56 overflow-hidden">
                        <img
                          src={buildImageUrl(course.image_url)}
                          alt={course.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(35,31,32,0),rgba(35,31,32,0.58))]" />
                      </div>
                      <CardContent className="bf-course-card-body flex flex-1 flex-col p-6">
                        <div className="flex flex-wrap gap-2 text-xs">
                          {course.level_name ? (
                            <span className="bf-ui-pill">
                              {course.level_name}
                            </span>
                          ) : null}
                          <span className="bf-ui-pill is-dark">
                            {course.modality}
                          </span>
                        </div>
                        <h3 className="bf-course-card-title mt-4 text-2xl font-semibold">{course.title}</h3>
                        <p className="mt-3 flex-1 text-sm leading-7 text-[#231f20]/70 dark:text-[#cfd7e6]">
                          {course.short_description}
                        </p>
                        <div className="mt-5 grid gap-3 text-sm text-[#231f20]/62 dark:text-[#cfd7e6]">
                          <div className="flex items-center gap-2">
                            <MonitorPlay className="h-4 w-4 text-[#6c8bc6]" /> {course.modality}
                          </div>
                          <div className="flex items-center gap-2">
                            <BookOpen className="h-4 w-4 text-[#6c8bc6]" /> {course.course_type_name || course.language_name}
                          </div>
                        </div>
                        <div className="mt-6">
                          <Button className="bf-premium-cta bf-ui-button h-11 rounded-full bg-[#6c8bc6] px-5 text-sm font-semibold text-white hover:bg-[#5876b0]">
                            {t('courses.details')} <ChevronRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
